import { get, isEmpty } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import { getFieldIdByUuid } from 'fields/selectors';
import {
  allOverlayQuickSelectMenuOptions,
  fieldHealthLayers,
  fieldViewLayers,
  nonSatLayers,
  overlayControls,
  overlayLayerGroups,
  overlayLayers,
  seasonlessOverlayLayers,
  soilSampleLayers,
  ssurgoLayers,
  importedMapsLayers
} from 'constants/imagery';
import { getActiveFieldId } from 'current/selectors';
import { getMosaicV3FHIFlag } from 'user/selectors';

export const getOverlayDateOf = ({ state, fieldId, layer, uuid }) => {
  return get(state, `imagery['${fieldId}'].${layer}['${uuid}'].date`) || uuid;
};


export const getUsableOverlayLayerGroups = (state) => {
  const fieldId = getActiveFieldId(state);
  const usable = [];
  overlayLayerGroups.forEach( (group) => {
    let usableLayers = [];
    group.layers.forEach( (layer) => {
      if (get(state, `imagery['${fieldId}'][${layer}]`)) {
        usableLayers.push(layer);
      }
    });
    if (usableLayers.length) {
      usable.push({...group, layers: usableLayers});
    }
  });
  return usable;
};

export const getOverlayLayerGroupTypeOfLayer = ({ state, layer }) => {
  const groups = getUsableOverlayLayerGroups(state);
  return get(groups.filter( group => [...group.layers].includes(layer) ), '[0]');
};

export const getImagesOfLayerForField = ({ state, fieldId, season, uuid, layer }) => {
  const mosaicV3FHIFlag = getMosaicV3FHIFlag(state);
  if(uuid) {
    return get(state, `imagery.${fieldId}.${layer}['${uuid}']`);
  }

  let layerImages = Object.values( get(state, `imagery.${fieldId}.${layer}`, {}));
  if(season) {
    layerImages = layerImages.filter( (image ) => image.displaySeason === season );
  }
  return mosaicV3FHIFlag ? layerImages.reverse() : layerImages;
};

export const getImageUuidsOfLayerForField = ({ state, fieldId, season, layer }) => {
  let layerImages = getImagesOfLayerForField({ state, fieldId, season, layer });
  if(season) {
    layerImages = layerImages.filter( (image ) => image.displaySeason === season );
  }
  return layerImages.map( (image ) => {
    return image.overlayUuid;
  }).sort().reverse(); // and order them (has date in front to sort)
};

export const getImageDatesOfLayerForField = ({ state, fieldId, season, layer }) => {
  const layerImages = getImagesOfLayerForField({ state, fieldId, season, layer });
  const dates = layerImages.map( (image ) => {
    return image.date; // get the dates
  }).sort().reverse(); // and order them
  return dates;
};

export const getImageSeasonsOfLayerForField = ({ state, fieldId, layer }) => {
  const layerImages = getImagesOfLayerForField({ state, fieldId, layer });
  const seasons = {};
  layerImages.forEach( (image ) => {
    if(image.displaySeason) { // Not all images are seasonal (e.g. SSURGO)
      seasons[image.displaySeason] = true; // get unique seasons
    }
  });
  return Object.keys(seasons).sort().reverse(); // and order them
};

export const getCropFromActiveImage = ({ state, fieldId, layer, season }) => {
  const layerImages = getImagesOfLayerForField({ state, fieldId, layer });
  const image = layerImages.find((currentImage) => currentImage.displaySeason === season);

  // using lodash.get to handle undefined scenarios as we don't have optional chaining
  return get(image, 'crop');
};

export const getFieldViewLegendForFieldImage = ({
  state,
  fieldId,
  fieldUuid,
  layer,
  overlayUuid
}) => {
  fieldId = fieldId || getFieldIdByUuid(state, fieldUuid); // FV doesn't include fieldId
  const layerData = get(state, `imagery.${fieldId}.${layer}`);
  return get(layerData, overlayUuid, {}).legend;
};

export const getFieldViewTileUrlForFieldImage = (
  state,
  fieldId,
  fieldUuid,
  layer,
  overlayUuid
) => {
  fieldId = fieldId || getFieldIdByUuid(state, fieldUuid); // FV doesn't include fieldId
  const layerData = get(state, `imagery.${fieldId}.${layer}`);
  return get(layerData, overlayUuid, {}).url;
};

export const getFieldHealthImageryByFieldId = (state, fieldId) => {
  const matchingImages = {};
  const allImages = get(state, `imagery['${fieldId}']`, {});
  Object.keys(allImages).forEach( (imageType) => {
    if( Object.values(fieldHealthLayers).includes(imageType) ) {
      matchingImages[imageType] = allImages[imageType];
    }
  });
  return matchingImages;
};

export const getScoutingImageryForActiveField = createSelector([
  state => getActiveFieldId(state),
  state => state.imagery
],
(fieldId, imagery) => imagery[fieldId][fieldHealthLayers.SCOUTING]);

export const getSsurgoImageryByFieldId = (state, fieldId) => {
  const matchingImages = {};
  const allImages = get(state, `imagery['${fieldId}']`, {});
  Object.keys(allImages).forEach( (imageType) => {
    if( Object.values(ssurgoLayers).includes(imageType) ) {
      matchingImages[imageType] = allImages[imageType];
    }
  });
  return matchingImages;
};

export const getSsurgoZonesByFieldId = (state, fieldId) => {
  // Converts the imagery state for ssurge to a single usable geoJson
  const zoneSets = getSsurgoImageryByFieldId( state, fieldId );
  const layerData = Object.values(zoneSets.soil)[0]; // There can be only one...in ssurgo data
  return layerData;
};

export const getSoilSampleImageryByFieldId = (state, fieldId) => {
  const matchingImages = {};
  const allImages = get(state, `imagery['${fieldId}']`, {});
  Object.keys(allImages).forEach( (imageType) => {
    if( Object.values(soilSampleLayers).includes(imageType) ) {
      matchingImages[imageType] = allImages[imageType];
    }
  });
  return matchingImages;
};

export const getNonSatImageryByFieldId = (state, fieldId) => {
  const allImages = get(state, `imagery['${fieldId}']`, {});
  const matchingImages = {};
  Object.keys(allImages).forEach( (imageType) => {
    if( Object.values(nonSatLayers).includes(imageType) ) {
      matchingImages[imageType] = allImages[imageType];
    }
  });
  return matchingImages;
};

export const getFieldViewImageryByFieldId = (state, fieldId) => {
  const allImages = get(state, `imagery['${fieldId}']`, {});
  const matchingImages = {};
  Object.keys(allImages).forEach( (imageType) => {
    if( Object.values(fieldViewLayers).includes(imageType) ) {
      matchingImages[imageType] = allImages[imageType];
    }
  });
  return matchingImages;
};

export const fieldHasSsurgoZones = (state, fieldId) => {
  return !isEmpty(getSsurgoImageryByFieldId(state, fieldId));
};

export const getFieldLayerSeasonLabel = ({ state, fieldId, layer}) => {
  const allOverlayUuids = getImageUuidsOfLayerForField({ state, fieldId, layer });
  const overlayUuid = allOverlayUuids[0];
  const overlay = getImagesOfLayerForField({ state, fieldId, uuid: overlayUuid, layer }) || {};
  return {
    layer: overlay.layer || layer,
    overlayUuid,
    season: overlay.displaySeason || overlay.year || null
  };
};

export const getOverlayQuickSelectMenuOptions = (state) => {
  const fieldId = getActiveFieldId(state);

  let currentLabels = allOverlayQuickSelectMenuOptions.map( layer => {
    return getFieldLayerSeasonLabel({ state, fieldId, layer });
  });

  currentLabels = currentLabels.filter( ({ layer, season }) => {
    const hasData = layer === overlayLayers.PRODUCT_RATE || getImagesOfLayerForField({ state, fieldId, layer }).length;
    const hasSeason = season || seasonlessOverlayLayers[layer];
    return hasSeason && hasData;
  });

  return currentLabels.slice(0, overlayControls.OVERLAY_QUICK_MENU_MAX);
};

export const getImportedMapsImageryByFieldId = (state, fieldId) => {
  const matchingImages = {};
  const allImages = get(state, `imagery['${fieldId}']`, {});
  Object.keys(allImages).forEach( (imageType) => {
    if( Object.values(importedMapsLayers).includes(imageType) ) {
      matchingImages[imageType] = allImages[imageType];
    }
  });
  return matchingImages;
};

export const getImportedMapsZonesByFieldId = (state, fieldId) => {
  const importedMaps = getImportedMapsImageryByFieldId(state, fieldId);
  if (importedMaps.imported_maps) {
    return Object.values(importedMaps.imported_maps).sort((a, b) => {
      const nameA = a.meta.name?.toLowerCase();
      const nameB = b.meta.name?.toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }

  return [];
};

export const getCompleteMapLayers = (state) => get(state, 'manual.completeMapLayers.completeLayers');
